/**
 * The trials of one arm as small buttons, one per trial, colored by result. A trial with a
 * judge flag has a flag, and one with a pending suggestion from Claude has a dot, so
 * nothing is hidden behind a trial that is not shown.
 */
import { Flag } from 'lucide-react';
import type { EvalSuggestion, StoredTrialResult } from '../../types/evalTypes';

/** The trial to show first: the first that failed, as it is usually the one to read. */
export const defaultTrial = (results: StoredTrialResult[]): StoredTrialResult | undefined =>
  results.find((result) => result.status === 'error' || (result.status === 'ok' && !result.passed)) ?? results[0];

const tone = (result: StoredTrialResult): string => {
  if (result.status === 'skipped') return 'border-gray-200 bg-gray-100 text-gray-500';
  if (result.status === 'error' || !result.passed) return 'border-red-300 bg-red-50 text-red-900';
  return 'border-gray-300 bg-white text-gray-800';
};

const label = (result: StoredTrialResult): string => {
  if (result.status === 'skipped') return 'skipped';
  if (result.status === 'error') return 'error';
  return result.passed ? 'passed' : 'failed';
};

interface TrialPickerProps {
  results: StoredTrialResult[];
  selectedId: string | undefined;
  /** Pending suggestions of the run; the ones for these trials get a dot. */
  suggestions: EvalSuggestion[];
  onSelect: (resultId: string) => void;
}

export const TrialPicker = ({ results, selectedId, suggestions, onSelect }: TrialPickerProps) => (
  <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Trials">
    {results.map((result) => {
      const selected = result.resultId === selectedId;
      const pending = suggestions.some((suggestion) => suggestion.status === 'pending' && suggestion.kind === 'judge-flag' && suggestion.resultId === result.resultId);
      return (
        <button
          key={result.resultId}
          type="button"
          role="tab"
          aria-selected={selected}
          title={`Trial ${result.trial}: ${label(result)}${result.judgeFlag ? ', judge flagged' : ''}${pending ? ', suggestion from Claude' : ''}`}
          onClick={() => onSelect(result.resultId)}
          className={`relative inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${tone(result)} ${
            selected ? 'ring-2 ring-blue-500 ring-offset-1' : 'opacity-80 hover:opacity-100'
          }`}
        >
          Trial {result.trial}
          {result.judgeFlag && <Flag size={11} className="text-amber-700" aria-hidden />}
          {pending && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-violet-600" aria-hidden />}
        </button>
      );
    })}
  </div>
);
